import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Row, Col } from "antd";
import { AuthContext } from "../../../../services/auth.service";
import { hasFeatureAccess } from "../../../../utils/subscriptionUtils";
import { isRowAnomalous } from "../../../TagMonitorDash";
import { useTagMonitorData } from "../../hooks/useTagMonitorData";
import { getDashboardDates, getTodayPeriod, formatMinutes, formatNumber, sumBy } from "../../utils";
import {
  ANOMALIES_INFO,
  INACTIVE_TAGS_INFO,
  TAG_EVENTS_CHART_INFO,
  TAG_EVENTS_FIRED_INFO,
  TAG_MONITORED_INFO,
  TAG_RECENT_ERRORS_INFO,
  TOP_ERRORS_INFO,
} from "../../tooltips";
import SectionHeader from "../SectionHeader";
import HomeCard from "../HomeCard";
import InfoTooltip from "../InfoTooltip";
import HomeTable from "../HomeTable";
import TagMonitorChart from "../charts/TagMonitorChart";
import "./styles.scss";

const DASHBOARD_LINK = "/tag_monitor_dashboard";
const TOP_ROWS = 5;
const LOAD_ERROR_MESSAGE = "Tag Monitor data could not be loaded.";

// Error % from which the dot turns red or orange, below it the dot is purple
const HIGH_ERROR_RATE = 7.5;
const MEDIUM_ERROR_RATE = 2.5;

// Severity order and colors of the anomaly table on the Tag Monitor dashboard
const SEVERITY_RANK = { critical: 3, warning: 2, info: 1, normal: 0 };
const SEVERITY_COLORS = { critical: "red", warning: "orange", info: "blue" };

// A failed request must not look like "nothing found"
const emptyMessage = (context, message) => (context === null ? LOAD_ERROR_MESSAGE : message);

// A rule without containers (or a plain value) hides the tag everywhere; the dashboard ignores containers for now
export const withoutExcludedTags = (rows = [], context) => {
  const rules = (
    context?.tags_monitoring_exclude_rules || [
      ...(context?.tags_monitoring_exclude_client || []),
      ...(context?.tags_monitoring_exclude_server || []),
    ]
  ).map((rule) => (typeof rule === "string" ? { value: rule, containers: null } : rule));

  return rows.filter(
    (row) =>
      !rules.some(
        (rule) =>
          (rule.value === row.tag_name || rule.value === row.tag_id) &&
          // A missing tag of a rule on all containers has no container, any exclusion of the tag hides it
          (!rule.containers?.length ||
            (row.not_seen && !row.container_id) ||
            rule.containers.includes(row.container_id)),
      ),
  );
};

// Highest error % per tag, grouped like the error table on the Tag Monitor dashboard
const getTopErrors = (rows) => {
  const tags = {};

  rows.forEach((row) => {
    const key = `${row.tag_id}-${row.container_id}-${row.event_name}-${row.market ?? ""}`;
    if (!tags[key]) {
      tags[key] = {
        key,
        tag_id: row.tag_id,
        tag_name: row.tag_name,
        container_id: row.container_id,
        event_name: row.event_name,
        calls: 0,
        fails: 0,
      };
    }
    tags[key].calls += row.total_calls || 0;
    tags[key].fails += row.total_fails || 0;
  });

  return Object.values(tags)
    .filter((tag) => tag.fails > 0)
    .map((tag) => ({ ...tag, errorRate: (tag.fails / tag.calls) * 100 }))
    .sort((a, b) => b.errorRate - a.errorRate)
    .slice(0, TOP_ROWS);
};

// Anomalies per tag, counted and ranked like the anomaly table on the Tag Monitor dashboard
const getTopAnomalies = (context) => {
  const tags = {};

  (context?.anomaly_data_client || []).forEach((row) => {
    const key = `${row.tagid || ""}-${row.containerid || ""}-${row.hostname || ""}`;
    if (!tags[key]) {
      tags[key] = {
        key,
        tag_id: row.tagid,
        tag_name: row.tagname,
        container_id: row.containerid,
        hostname: row.hostname,
        anomalies: 0,
        severity: "normal",
      };
    }
    if (isRowAnomalous(row)) tags[key].anomalies += 1;

    // Like the dashboard, the severity is taken from every row the model flagged
    if (Number(row.anomaly) !== 0 && (SEVERITY_RANK[row.severity] || 0) > SEVERITY_RANK[tags[key].severity]) {
      tags[key].severity = row.severity;
    }
  });

  // Excluded tags are matched on tag_id and tag_name, which the anomaly rows only have after grouping
  return withoutExcludedTags(Object.values(tags), context)
    .filter((tag) => tag.anomalies > 0)
    .sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || b.anomalies - a.anomalies)
    .slice(0, TOP_ROWS);
};

// Unnamed tags show their ID and a link to name them; always with the container, names are unique per container
export const renderTagName = ({ tag_name: tagName, tag_id: id, container_id: containerId, not_seen: notSeen }) => {
  const name = tagName && tagName !== "undefined" ? tagName : null;

  return (
    <span className="tag-monitor-tag" title={containerId ? `${name ?? id} (${containerId})` : name ?? id}>
      <span className="tag-monitor-tag-name">{name ?? id}</span>
      {/* A tag without any call has no name in the data, it isn't unnamed */}
      {!name && !notSeen && (
        <Link to="/authorize_user" className="tag-monitor-rename-tag">
          Name tag
        </Link>
      )}
      {containerId && <span className="tag-monitor-container">{containerId}</span>}
    </span>
  );
};

const renderEventName = ({ event_name: eventName }) => eventName || "(no name)";

const renderErrorRate = ({ errorRate }) => {
  const color = errorRate >= HIGH_ERROR_RATE ? "red" : errorRate >= MEDIUM_ERROR_RATE ? "orange" : "purple";

  return (
    <span className="tag-monitor-error-rate">
      {errorRate.toFixed(1)}%
      <span className={`tag-monitor-dot tag-monitor-dot-${color}`} />
    </span>
  );
};

// "14 h ago", or "No call in 8 days" for a tag of a rule that has no call in the data
const renderLastFired = ({ minutes_ago: minutes, not_seen: notSeen }) => (
  <span className="tag-monitor-pill tag-monitor-pill-red">
    {notSeen ? `No call in ${formatMinutes(minutes)}` : `${formatMinutes(minutes)} ago`}
  </span>
);

// The client's own inactivity rule, a tag without a rule counts as inactive after a day
const renderInactivityRule = ({ reason, rule, threshold }) =>
  reason === "configured" ? rule || `After ${formatMinutes(threshold)}` : "No rule";

const renderAnomalies = ({ anomalies, severity }) => (
  <span className={`tag-monitor-pill tag-monitor-pill-${SEVERITY_COLORS[severity] || "blue"}`}>
    {formatNumber(anomalies)}
  </span>
);

const ERROR_COLUMNS = [
  { key: "tag_name", title: "Tag name", width: "45%", render: renderTagName },
  { key: "event_name", title: "Event name", width: "35%", render: renderEventName },
  { key: "error_rate", title: "Error %", align: "right", render: renderErrorRate },
];

const INACTIVITY_COLUMNS = [
  { key: "tag_name", title: "Tag name", width: "45%", render: renderTagName },
  { key: "rule", title: "Rule", width: "30%", render: renderInactivityRule },
  { key: "last_fired", title: "Last fired", align: "right", render: renderLastFired },
];

const ANOMALY_COLUMNS = [
  { key: "tag_name", title: "Tag name", width: "45%", render: renderTagName },
  { key: "hostname", title: "Hostname", width: "35%", render: ({ hostname }) => hostname || "-" },
  { key: "anomalies", title: "Anomalies", align: "right", render: renderAnomalies },
];

/**
 * TagMonitorSection Component - Tag Monitor section of the homepage
 *
 * @param {object} period - Period of the date filter, see getPeriod on the homepage
 * @param {object|null|undefined} errorsContext - Tag Monitor errors of the period, loaded once on the homepage
 * @param {function} onLoadingChange - Called with true or false when the loading state changes, for the loading screen
 */
const TagMonitorSection = ({ period, errorsContext, onLoadingChange }) => {
  const { companyInfo } = useContext(AuthContext);

  // Client-side / server-side view
  const [view, setView] = useState("client");

  // Fall back to client-side when the company has no server-side monitoring (e.g. after switching company)
  const hasServerSide = Boolean(companyInfo?.tag_monitor_server);
  const side = hasServerSide ? view : "client";
  const sideLabel = side === "server" ? "Server-side" : "Client-side";

  // Anomaly detection needs the Advanced plan or higher, like on the dashboard
  const hasAnomalyAccess = hasFeatureAccess(companyInfo?.tag_monitor_subscription_model, "advanced");

  const anomalyContext = useTagMonitorData("tag-anomaly", period, hasAnomalyAccess, "Home Tag Monitor");

  // Inactivity is about now, so it ignores the date filter (the same request as the quick actions)
  const todayPeriod = useMemo(() => getTodayPeriod(), []);
  const inactivityContext = useTagMonitorData("tag-inactivity", todayPeriod, true, "Home Tag Monitor");

  // The homepage shows its loading screen until every request is done, without access the anomalies are not requested
  const loading =
    errorsContext === undefined ||
    inactivityContext === undefined ||
    (hasAnomalyAccess && anomalyContext === undefined);
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Anomalies are only shown client-side, and not when the company has no anomaly model
  const showAnomalies =
    hasAnomalyAccess && side === "client" && anomalyContext?.anomaly_client_available !== false;

  const errorRows = useMemo(
    () => withoutExcludedTags(errorsContext?.[`error_query_${side}`], errorsContext),
    [errorsContext, side],
  );

  // Totals of the period for the banner and the stat cards, "-" instead of 0 while loading or when the load failed
  const totalsUnknown = !errorsContext;
  const totals = useMemo(() => {
    const errors = sumBy(errorRows, "total_fails");

    return {
      successfulEvents: sumBy(errorRows, "total_calls") - errors,
      eventsFired: new Set(errorRows.map((row) => row.event_name)).size,
      errors,
    };
  }, [errorRows]);

  const topErrors = useMemo(() => getTopErrors(errorRows), [errorRows]);

  // Totals per day, or per hour for today and yesterday: then the chart only shows the time, padded so the hours sort
  const chartRows = useMemo(() => {
    const rows = errorsContext?.[`error_daily_${side}`] || [];
    if (!period.window) return rows;
    return rows
      .map((row) => ({ ...row, timestamp: dayjs(row.timestamp).format("HH:mm") }))
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }, [errorsContext, side, period]);

  // "View more" opens the dashboard on the same period
  const dashboardDates = getDashboardDates(period);
  // In the order of the backend: tags with an inactivity rule first, then the most recently fired
  const inactiveTags = useMemo(
    () =>
      withoutExcludedTags(inactivityContext?.inactive_tags, inactivityContext)
        .filter((tag) => tag.data_type === side)
        .slice(0, TOP_ROWS)
        .map((tag) => ({ ...tag, key: `${tag.container_id}-${tag.tag_id}` })),
    [inactivityContext, side],
  );
  const topAnomalies = useMemo(() => getTopAnomalies(anomalyContext), [anomalyContext]);

  return (
    <section className="tag-monitor-section">
      <SectionHeader
        title="Tag Monitor"
        color="purple"
        configureLink="/tag_monitor_config"
        showToggle
        toggleValue={side}
        onToggleChange={setView}
        serverDisabled={!hasServerSide}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <div className="monitored-banner">
            <span className="monitored-banner-value">
              {totalsUnknown ? "-" : formatNumber(totals.successfulEvents)}
            </span>
            <span className="monitored-banner-text">
              Tag events successfully monitored
              {/* <InfoTooltip text={TAG_MONITORED_INFO} /> */}
            </span>
            <span className="monitored-banner-circle" />
          </div>
        </Col>
        <Col xs={12} lg={4}>
          <HomeCard className="stat-card">
            <span className="stat-card-label">
              Events fired
              <InfoTooltip text={TAG_EVENTS_FIRED_INFO} />
            </span>
            <span className="stat-card-value">{totalsUnknown ? "-" : formatNumber(totals.eventsFired)}</span>
          </HomeCard>
        </Col>
        <Col xs={12} lg={4}>
          <HomeCard className="stat-card">
            <span className="stat-card-label">
              Recent errors
              <InfoTooltip text={TAG_RECENT_ERRORS_INFO} />
            </span>
            <span className="stat-card-value stat-card-value-error">
              {totalsUnknown ? "-" : formatNumber(totals.errors)}
            </span>
          </HomeCard>
        </Col>
      </Row>

      {/* Without anomalies the chart takes the full width and both tables move to the second row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={showAnomalies ? 12 : 24}>
          <HomeCard
            title={`${sideLabel} - Tag events`}
            // info={TAG_EVENTS_CHART_INFO}
            viewMore
            viewMoreLink={`${DASHBOARD_LINK}?view=${side}&${dashboardDates}`}
          >
            <TagMonitorChart
              serverData={chartRows}
              title=""
              loading={errorsContext === undefined}
            />
          </HomeCard>
        </Col>
        <Col xs={24} lg={12}>
          <HomeCard
            title={`${sideLabel} - Top ${TOP_ROWS} errors`}
            info={TOP_ERRORS_INFO}
            viewMore
            viewMoreLink={`${DASHBOARD_LINK}?view=${side}&${dashboardDates}`}
          >
            <HomeTable
              columns={ERROR_COLUMNS}
              rows={topErrors}
              loading={errorsContext === undefined}
              emptyMessage={emptyMessage(errorsContext, `No tag errors ${period.label}.`)}
            />
          </HomeCard>
        </Col>
        <Col xs={24} lg={12}>
          <HomeCard
            title={`${sideLabel} - Inactive tags`}
            info={INACTIVE_TAGS_INFO}
            viewMore
            viewMoreLink={`${DASHBOARD_LINK}?view=${side}&tab=tag_volume`}
          >
            <HomeTable
              columns={INACTIVITY_COLUMNS}
              rows={inactiveTags}
              loading={inactivityContext === undefined}
              emptyMessage={emptyMessage(inactivityContext, "No inactive tags.")}
            />
          </HomeCard>
        </Col>
        {showAnomalies && (
          <Col xs={24} lg={12}>
            <HomeCard
              title={`Top ${TOP_ROWS} anomalies detected`}
              // info={ANOMALIES_INFO}
              viewMore
              viewMoreLink={`${DASHBOARD_LINK}?view=client&tab=anomaly_detection&${dashboardDates}`}
            >
              <HomeTable
                columns={ANOMALY_COLUMNS}
                rows={topAnomalies}
                loading={anomalyContext === undefined}
                emptyMessage={emptyMessage(anomalyContext, `No anomalies ${period.label}.`)}
              />
            </HomeCard>
          </Col>
        )}
      </Row>
    </section>
  );
};

export default TagMonitorSection;
